import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { fetchClients } from '../store/clientsSlice';
import parseDateToUtcIso from '../utils/date';
import './CapitalAssetsPage.css';

const apiUrl = process.env.REACT_APP_API_URL;

const fetchCapitalAssetTransactionsRequest = (clientId, financialYear) => axios.get(`${apiUrl}/api/capital-assets`, {
  params: { clientId, financialYear },
});

const emptyForm = {
  assetName: '',
  assetClass: 'EQUITY_MUTUAL_FUND',
  acquisitionDate: '',
  transferDate: '',
  quantity: '',
  saleConsideration: '',
  acquisitionCost: '',
  transferExpenses: '',
  costOfImprovement: '',
  fairMarketValueAsOn31Jan2018: '',
  listed: 'true',
  sttApplicable: 'true',
  acquisitionMode: 'PURCHASE',
  notes: '',
};

const formatAmount = (value) => Number(value ?? 0).toLocaleString('en-IN', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const formatDate = (value) => {
  if (!value) {
    return '-';
  }
  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    timeZone: 'Asia/Kolkata',
  }).format(new Date(value));
};

const CapitalAssetsPage = () => {
  const dispatch = useDispatch();
  const { clientId, financialYear } = useSelector((state) => state.app);
  const [form, setForm] = useState(emptyForm);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const fetchItems = () => {
    if (!clientId || !financialYear) {
      return;
    }

    setLoading(true);
    fetchCapitalAssetTransactionsRequest(clientId, financialYear)
      .then((response) => {
        setItems(response.data);
        setError('');
      })
      .catch((requestError) => {
        console.error('Error fetching capital asset transactions', requestError);
        setError('Error fetching capital asset transactions.');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    dispatch(fetchClients());
  }, [dispatch]);

  useEffect(() => {
    if (!clientId || !financialYear) {
      return;
    }

    setLoading(true);
    fetchCapitalAssetTransactionsRequest(clientId, financialYear)
      .then((response) => {
        setItems(response.data);
        setError('');
      })
      .catch((requestError) => {
        console.error('Error fetching capital asset transactions', requestError);
        setError('Error fetching capital asset transactions.');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [clientId, financialYear]);

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!clientId) {
      setError('Please select a client first.');
      return;
    }

    setSaving(true);
    axios.post(`${apiUrl}/api/capital-assets`, {
      clientId,
      assetName: form.assetName,
      assetClass: form.assetClass,
      acquisitionDate: parseDateToUtcIso(form.acquisitionDate),
      transferDate: parseDateToUtcIso(form.transferDate),
      quantity: form.quantity || null,
      saleConsideration: form.saleConsideration || null,
      acquisitionCost: form.acquisitionCost || null,
      transferExpenses: form.transferExpenses || null,
      costOfImprovement: form.costOfImprovement || null,
      fairMarketValueAsOn31Jan2018: form.fairMarketValueAsOn31Jan2018 || null,
      listed: form.listed === 'true',
      sttApplicable: form.sttApplicable === 'true',
      acquisitionMode: form.acquisitionMode,
      notes: form.notes,
    })
      .then(() => {
        setForm(emptyForm);
        setSuccess('Capital asset transaction saved.');
        setError('');
        fetchItems();
      })
      .catch((requestError) => {
        console.error('Error saving capital asset transaction', requestError);
        setError('Error saving capital asset transaction.');
        setSuccess('');
      })
      .finally(() => {
        setSaving(false);
      });
  };

  return (
    <div className="capital-assets-page">
      <h2>Capital Asset Transactions</h2>
      <p className="financial-page-subtitle">Use this register for equity mutual funds now and as the long-term base for future capital-asset classes.</p>

      <form className="capital-assets-form" onSubmit={handleSubmit}>
        <label>
          Asset Name
          <input value={form.assetName} onChange={(event) => setForm((current) => ({ ...current, assetName: event.target.value }))} required />
        </label>
        <label>
          Asset Class
          <select value={form.assetClass} onChange={(event) => setForm((current) => ({ ...current, assetClass: event.target.value }))}>
            <option value="EQUITY_MUTUAL_FUND">Equity Mutual Fund</option>
            <option value="LISTED_EQUITY">Listed Equity</option>
          </select>
        </label>
        <label>
          Acquisition Date
          <input type="date" value={form.acquisitionDate} onChange={(event) => setForm((current) => ({ ...current, acquisitionDate: event.target.value }))} required />
        </label>
        <label>
          Transfer Date
          <input type="date" value={form.transferDate} onChange={(event) => setForm((current) => ({ ...current, transferDate: event.target.value }))} required />
        </label>
        <label>
          Quantity / Units
          <input type="number" step="0.0001" value={form.quantity} onChange={(event) => setForm((current) => ({ ...current, quantity: event.target.value }))} />
        </label>
        <label>
          Acquisition Cost
          <input type="number" step="0.01" value={form.acquisitionCost} onChange={(event) => setForm((current) => ({ ...current, acquisitionCost: event.target.value }))} required />
        </label>
        <label>
          Sale Consideration
          <input type="number" step="0.01" value={form.saleConsideration} onChange={(event) => setForm((current) => ({ ...current, saleConsideration: event.target.value }))} required />
        </label>
        <label>
          Transfer Expenses
          <input type="number" step="0.01" value={form.transferExpenses} onChange={(event) => setForm((current) => ({ ...current, transferExpenses: event.target.value }))} />
        </label>
        <label>
          Cost Of Improvement
          <input type="number" step="0.01" value={form.costOfImprovement} onChange={(event) => setForm((current) => ({ ...current, costOfImprovement: event.target.value }))} />
        </label>
        <label>
          31-Jan-2018 FMV
          <input type="number" step="0.01" value={form.fairMarketValueAsOn31Jan2018} onChange={(event) => setForm((current) => ({ ...current, fairMarketValueAsOn31Jan2018: event.target.value }))} />
        </label>
        <label>
          Listed
          <select value={form.listed} onChange={(event) => setForm((current) => ({ ...current, listed: event.target.value }))}>
            <option value="true">Yes</option>
            <option value="false">No</option>
          </select>
        </label>
        <label>
          STT Applicable
          <select value={form.sttApplicable} onChange={(event) => setForm((current) => ({ ...current, sttApplicable: event.target.value }))}>
            <option value="true">Yes</option>
            <option value="false">No</option>
          </select>
        </label>
        <label>
          Acquisition Mode
          <select value={form.acquisitionMode} onChange={(event) => setForm((current) => ({ ...current, acquisitionMode: event.target.value }))}>
            <option value="PURCHASE">Purchase</option>
            <option value="GIFT">Gift</option>
            <option value="INHERITANCE">Inheritance</option>
          </select>
        </label>
        <label className="capital-assets-form__full">
          Notes
          <textarea rows="3" value={form.notes} onChange={(event) => setForm((current) => ({ ...current, notes: event.target.value }))} />
        </label>
        {error && <p className="financial-message financial-message--error capital-assets-form__full">{error}</p>}
        {success && <p className="financial-message financial-message--success capital-assets-form__full">{success}</p>}
        <button className="financial-button capital-assets-form__full" type="submit" disabled={saving}>{saving ? 'Saving...' : 'Save Transaction'}</button>
      </form>

      {loading && <p className="financial-message">Loading capital asset transactions...</p>}

      {items.length > 0 ? (
        <div className="table-container">
          <table className="capital-assets-table">
            <thead>
              <tr>
                <th>Asset</th>
                <th>Class</th>
                <th>Acquired</th>
                <th>Transferred</th>
                <th>Acquisition Cost</th>
                <th>Sale Consideration</th>
                <th>Transfer Expenses</th>
                <th>31-Jan-2018 FMV</th>
                <th>Listed</th>
                <th>STT</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id}>
                  <td>{item.assetName}</td>
                  <td>{item.assetClass}</td>
                  <td>{formatDate(item.acquisitionDate)}</td>
                  <td>{formatDate(item.transferDate)}</td>
                  <td>₹ {formatAmount(item.acquisitionCost)}</td>
                  <td>₹ {formatAmount(item.saleConsideration)}</td>
                  <td>₹ {formatAmount(item.transferExpenses)}</td>
                  <td>{item.fairMarketValueAsOn31Jan2018 != null ? `₹ ${formatAmount(item.fairMarketValueAsOn31Jan2018)}` : '-'}</td>
                  <td>{item.listed ? 'Yes' : 'No'}</td>
                  <td>{item.sttApplicable ? 'Yes' : 'No'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : !loading ? (
        <p className="financial-message">No capital asset transactions found for the selected financial year.</p>
      ) : null}
    </div>
  );
};

export default CapitalAssetsPage;